import type { Ref } from 'react';
import React, { useMemo } from 'react';
import type { TableReportHeader, TableReportProps, TableReportRow } from 'c-reports/Types';

import type { TableCellProps, TableHeadProps } from '@mui/material';
import { Box, TableBody, TableHead, Typography } from '@mui/material';
import { Button, LoadingSpinner, Table as MuiTable, TableCell, TableRow } from 'c-components';
import { NetworkRequestState } from 'c-data-layer';
import { Translate, useCommonTranslation } from 'c-translation';
import { paperSurface } from 'c-lib';
import ReportLoadFailed from '../ReportLoadFailed';

type Props = TableReportProps<any> & {
    loadingState: NetworkRequestState;
    disableInteraction?: boolean;
    addLoadingBar?: boolean;
    refreshData?: () => void;
    /** Re-runs a failed request (refreshData also clears the advanced filters). */
    onRetry?: () => void;
};

const disabledSx: TableCellProps['sx'] = { pointerEvents: 'none' };

// Every sticky header cell, TableRow's side spacers included. A cell's own sx can't set a background
// on top of this (this selector is more specific), so style header content inside the cell instead.
const headerSurfaceSx =
    (elevation: number): TableHeadProps['sx'] =>
    theme => ({
        '& .MuiTableCell-stickyHeader': paperSurface(theme, elevation),
    });

const RenderStickers = ({ theHeadersStickers, colSx }) => {
    if (theHeadersStickers.length === 0) {
        return null;
    }
    return (
        <TableRow sx={colSx}>
            {theHeadersStickers.map((hs, hsi) => (
                <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={hsi}
                    sx={hs?.content?.props?.sx}
                    {...hs.tableCellProps}
                >
                    {hs.content}
                </TableCell>
            ))}
        </TableRow>
    );
};
const ReportTableWrapper = (
    {
        headerStickers,
        rows,
        headers,
        loadingState,
        disableInteraction = false,
        dense = false,
        addLoadingBar = false,
        refreshData,
        onRetry,
        headerSurfaceElevation,
    }: Props,
    ref: Ref<any>,
) => {
    const theHeaders = useMemo<TableReportHeader[]>(
        () => (Array.isArray(headers) ? headers : []),
        [headers],
    );
    const t = useCommonTranslation();
    const theHeadersStickers = useMemo(
        () => (Array.isArray(headerStickers) ? headerStickers : []),
        [headerStickers],
    );
    const actualRows = useMemo<TableReportRow[]>(() => (Array.isArray(rows) ? rows : []), [rows]);
    const failed = loadingState === NetworkRequestState.Error;
    const noData = useMemo(
        () =>
            loadingState === NetworkRequestState.Success &&
            Array.isArray(actualRows) &&
            actualRows.length === 0,
        [loadingState, actualRows],
    );

    const colSx = useMemo(() => (disableInteraction ? disabledSx : {}), [disableInteraction]);
    const headSx = useMemo(
        () =>
            headerSurfaceElevation == null ? undefined : headerSurfaceSx(headerSurfaceElevation),
        [headerSurfaceElevation],
    );

    return (
        <>
            {loadingState === NetworkRequestState.InProgress && addLoadingBar && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', zIndex: 1 }}>
                    <LoadingSpinner />
                </Box>
            )}
            {(!addLoadingBar || loadingState === NetworkRequestState.Success) && (
                <MuiTable stickyHeader ref={ref} size={dense ? 'small' : undefined}>
                    <TableHead sx={headSx}>
                        <RenderStickers theHeadersStickers={theHeadersStickers} colSx={colSx} />
                        <TableRow sx={colSx}>
                            {theHeaders.map((h, hi) => (
                                <TableCell
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={hi}
                                    {...h.tableCellProps}
                                    sx={h.sx}
                                >
                                    {h.content}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {actualRows.map((r, ri) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <TableRow sx={r.sx} key={ri}>
                                {r.content.map((c, ci) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableCell key={ci} sx={{ ...colSx, ...c.sx } as any}>
                                        {c.content}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </MuiTable>
            )}
            {failed && <ReportLoadFailed onRetry={onRetry} />}
            {noData && (
                <>
                    {refreshData ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            mt="10%"
                            color="grey.500"
                            gap={2}
                        >
                            <Typography variant="h2">
                                {t('Modules.Main.Campaigns.Overview.Reports.errorTitle')}
                            </Typography>
                            <Button onClick={refreshData} variant="text">
                                {t('Modules.Main.Campaigns.Overview.Reports.refresh')}
                            </Button>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: 'grey.500',
                                mt: 5,
                            }}
                        >
                            <Typography variant="h2">
                                <Translate path="Reporting.noData" />
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </>
    );
};

export default React.forwardRef(ReportTableWrapper);
